import { useState, useEffect, useCallback } from 'react';
import { conversionApi, ConversionTrendParams, ConversionSummaryParams } from '@/api/analysis';
import type {
  ConversionGoal,
  ConversionGoalCreate,
  ConversionTrendResponse,
  ConversionSummaryResponse,
} from '@/types/data';
import { useAuthStore } from '@/stores/authStore';

type ViewMode = 'goals' | 'summary' | 'trend';
type Aggregation = 'daily' | 'weekly' | 'monthly';

export default function Conversion() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('goals');

  // ゴール管理
  const [goals, setGoals] = useState<ConversionGoal[]>([]);
  const [isLoadingGoals, setIsLoadingGoals] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<ConversionGoal | null>(null);

  // ゴール作成/編集フォーム
  const [goalName, setGoalName] = useState('');
  const [goalDescription, setGoalDescription] = useState('');
  const [startPatterns, setStartPatterns] = useState<string[]>(['']);
  const [goalPatterns, setGoalPatterns] = useState<string[]>(['']);

  // 分析
  const [selectedGoalId, setSelectedGoalId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [aggregation, setAggregation] = useState<Aggregation>('daily');
  const [summaryResult, setSummaryResult] = useState<ConversionSummaryResponse | null>(null);
  const [trendResult, setTrendResult] = useState<ConversionTrendResponse | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // ゴール一覧取得
  const fetchGoals = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoadingGoals(true);
    try {
      const data = await conversionApi.getGoals(selectedCompanyId);
      setGoals(data);
      if (data.length > 0 && !selectedGoalId) {
        setSelectedGoalId(data[0].id);
      }
    } catch (error) {
      console.error('ゴール取得エラー:', error);
    } finally {
      setIsLoadingGoals(false);
    }
  }, [selectedCompanyId, selectedGoalId]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // ゴール作成/編集モーダルを開く
  const openGoalModal = (goal?: ConversionGoal) => {
    if (goal) {
      setEditingGoal(goal);
      setGoalName(goal.name);
      setGoalDescription(goal.description || '');
      setStartPatterns(goal.start_patterns.length > 0 ? goal.start_patterns : ['']);
      setGoalPatterns(goal.goal_patterns.length > 0 ? goal.goal_patterns : ['']);
    } else {
      setEditingGoal(null);
      setGoalName('');
      setGoalDescription('');
      setStartPatterns(['']);
      setGoalPatterns(['']);
    }
    setShowGoalModal(true);
  };

  // パターン追加
  const addPattern = (type: 'start' | 'goal') => {
    if (type === 'start') {
      setStartPatterns([...startPatterns, '']);
    } else {
      setGoalPatterns([...goalPatterns, '']);
    }
  };

  // パターン削除
  const removePattern = (type: 'start' | 'goal', index: number) => {
    if (type === 'start') {
      setStartPatterns(startPatterns.filter((_, i) => i !== index));
    } else {
      setGoalPatterns(goalPatterns.filter((_, i) => i !== index));
    }
  };

  // パターン更新
  const updatePattern = (type: 'start' | 'goal', index: number, value: string) => {
    if (type === 'start') {
      const newPatterns = [...startPatterns];
      newPatterns[index] = value;
      setStartPatterns(newPatterns);
    } else {
      const newPatterns = [...goalPatterns];
      newPatterns[index] = value;
      setGoalPatterns(newPatterns);
    }
  };

  // ゴール保存
  const saveGoal = async () => {
    if (!selectedCompanyId || !goalName) return;

    const validStartPatterns = startPatterns.filter((p) => p.trim());
    const validGoalPatterns = goalPatterns.filter((p) => p.trim());

    if (validStartPatterns.length === 0 || validGoalPatterns.length === 0) {
      alert('起点パターンとゴールパターンを少なくとも1つずつ入力してください');
      return;
    }

    const data: ConversionGoalCreate = {
      company_id: selectedCompanyId,
      name: goalName,
      description: goalDescription || undefined,
      start_patterns: validStartPatterns,
      goal_patterns: validGoalPatterns,
    };

    try {
      if (editingGoal) {
        await conversionApi.updateGoal(editingGoal.id, data);
      } else {
        await conversionApi.createGoal(data);
      }
      setShowGoalModal(false);
      fetchGoals();
    } catch (error) {
      console.error('ゴール保存エラー:', error);
      alert('ゴールの保存に失敗しました');
    }
  };

  // ゴール削除
  const deleteGoal = async (goalId: number) => {
    if (!confirm('このコンバージョンゴールを削除しますか？')) return;
    try {
      await conversionApi.deleteGoal(goalId);
      fetchGoals();
      if (selectedGoalId === goalId) {
        setSelectedGoalId(null);
      }
    } catch (error) {
      console.error('ゴール削除エラー:', error);
      alert('ゴールの削除に失敗しました');
    }
  };

  // サマリー取得
  const fetchSummary = async () => {
    if (!selectedCompanyId) return;
    setIsAnalyzing(true);
    try {
      const params: ConversionSummaryParams = {
        company_id: selectedCompanyId,
        start_date: startDate,
        end_date: endDate,
      };
      const result = await conversionApi.getSummary(params);
      setSummaryResult(result);
    } catch (error) {
      console.error('サマリー取得エラー:', error);
      alert('サマリーの取得に失敗しました');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // 推移取得
  const fetchTrend = async () => {
    if (!selectedGoalId) return;
    setIsAnalyzing(true);
    try {
      const params: ConversionTrendParams = {
        goal_id: selectedGoalId,
        start_date: startDate,
        end_date: endDate,
        aggregation: aggregation,
      };
      const result = await conversionApi.getTrend(params);
      setTrendResult(result);
    } catch (error) {
      console.error('推移取得エラー:', error);
      alert('推移データの取得に失敗しました');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // ビューモード切り替え時の処理
  useEffect(() => {
    if (viewMode === 'summary' && selectedCompanyId) {
      fetchSummary();
    } else if (viewMode === 'trend' && selectedGoalId) {
      fetchTrend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selectedGoalId, aggregation]);

  // 最大CVR取得
  const getMaxRate = () => {
    if (!trendResult) return 0;
    return Math.max(...trendResult.data.map((d) => d.conversion_rate), 0);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">コンバージョン分析</h1>

      {/* フィルター */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
            >
              <option value="">選択してください</option>
              {accessibleCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.display_name || company.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ゴール</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={selectedGoalId || ''}
              onChange={(e) => setSelectedGoalId(Number(e.target.value))}
              disabled={goals.length === 0}
            >
              <option value="">選択してください</option>
              {goals.map((goal) => (
                <option key={goal.id} value={goal.id}>
                  {goal.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">開始日</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">終了日</label>
            <input
              type="date"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">集計単位</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={aggregation}
              onChange={(e) => setAggregation(e.target.value as Aggregation)}
            >
              <option value="daily">日別</option>
              <option value="weekly">週別</option>
              <option value="monthly">月別</option>
            </select>
          </div>
        </div>
      </div>

      {/* タブ切り替え */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium ${
            viewMode === 'goals'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('goals')}
        >
          ゴール管理
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            viewMode === 'summary'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('summary')}
        >
          サマリー
        </button>
        <button
          className={`px-4 py-2 font-medium ${
            viewMode === 'trend'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('trend')}
          disabled={!selectedGoalId}
        >
          推移グラフ
        </button>
      </div>

      {/* ゴール管理ビュー */}
      {viewMode === 'goals' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">コンバージョンゴール一覧</h2>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              onClick={() => openGoalModal()}
              disabled={!selectedCompanyId}
            >
              新規作成
            </button>
          </div>
          <div className="p-4">
            {isLoadingGoals ? (
              <div className="text-center py-8">読み込み中...</div>
            ) : goals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                コンバージョンゴールがありません。新規作成してください。
              </div>
            ) : (
              <div className="space-y-4">
                {goals.map((goal) => (
                  <div key={goal.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-lg">{goal.name}</h3>
                        {goal.description && (
                          <p className="text-gray-600 text-sm">{goal.description}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          onClick={() => openGoalModal(goal)}
                        >
                          編集
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 text-sm"
                          onClick={() => deleteGoal(goal.id)}
                        >
                          削除
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">
                          起点パターン（分母）
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {goal.start_patterns.map((pattern, idx) => (
                            <span
                              key={idx}
                              className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm"
                            >
                              {pattern}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">
                          ゴールパターン（分子）
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {goal.goal_patterns.map((pattern, idx) => (
                            <span
                              key={idx}
                              className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm"
                            >
                              {pattern}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* サマリービュー */}
      {viewMode === 'summary' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">全ゴールサマリー</h2>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              onClick={fetchSummary}
              disabled={isAnalyzing || !selectedCompanyId}
            >
              {isAnalyzing ? '取得中...' : '更新'}
            </button>
          </div>
          <div className="p-4">
            {!summaryResult ? (
              <div className="text-center py-8 text-gray-500">
                法人を選択して更新ボタンをクリックしてください
              </div>
            ) : summaryResult.goals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                アクティブなゴールがありません
              </div>
            ) : (
              <div>
                <div className="mb-4 text-sm text-gray-600">
                  期間: {summaryResult.period.start_date} 〜 {summaryResult.period.end_date}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {summaryResult.goals.map((item) => (
                    <div
                      key={item.goal_id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 cursor-pointer"
                      onClick={() => {
                        setSelectedGoalId(item.goal_id);
                        setViewMode('trend');
                      }}
                    >
                      <h3 className="font-semibold text-lg mb-2">{item.goal_name}</h3>
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {item.conversion_rate}%
                      </div>
                      <div className="text-sm text-gray-600">
                        <span className="mr-4">起点: {item.start_count.toLocaleString()}</span>
                        <span>ゴール: {item.goal_count.toLocaleString()}</span>
                      </div>
                      {/* プログレスバー */}
                      <div className="mt-3 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min(item.conversion_rate, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 推移グラフビュー */}
      {viewMode === 'trend' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">コンバージョン率推移</h2>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              onClick={fetchTrend}
              disabled={isAnalyzing || !selectedGoalId}
            >
              {isAnalyzing ? '取得中...' : '更新'}
            </button>
          </div>
          <div className="p-4">
            {!trendResult ? (
              <div className="text-center py-8 text-gray-500">
                ゴールを選択してください
              </div>
            ) : trendResult.data.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                該当するデータがありません
              </div>
            ) : (
              <div>
                {/* サマリー */}
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">ゴール名</div>
                      <div className="font-semibold">{trendResult.goal_name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">総起点数</div>
                      <div className="font-semibold">
                        {trendResult.summary.total_start_count.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">総ゴール数</div>
                      <div className="font-semibold">
                        {trendResult.summary.total_goal_count.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">全体CVR</div>
                      <div className="font-bold text-2xl text-blue-600">
                        {trendResult.summary.overall_conversion_rate}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* 簡易グラフ */}
                <div className="mb-6">
                  <h3 className="font-semibold mb-3">CVR推移</h3>
                  <div className="h-48 flex items-end gap-1 border-b border-l border-gray-300 p-2">
                    {trendResult.data.map((point, idx) => {
                      const maxRate = getMaxRate();
                      const height = maxRate > 0 ? (point.conversion_rate / maxRate) * 100 : 0;
                      return (
                        <div
                          key={idx}
                          className="flex-1 flex flex-col items-center justify-end"
                          title={`${point.period}: ${point.conversion_rate}%`}
                        >
                          <div
                            className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors"
                            style={{ height: `${height}%`, minHeight: height > 0 ? '4px' : '0' }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{trendResult.data[0]?.period}</span>
                    <span>{trendResult.data[trendResult.data.length - 1]?.period}</span>
                  </div>
                </div>

                {/* データテーブル */}
                <div>
                  <h3 className="font-semibold mb-3">詳細データ</h3>
                  <div className="overflow-x-auto max-h-80">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            期間
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            起点数
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            ゴール数
                          </th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                            CVR
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {trendResult.data.map((point, idx) => (
                          <tr key={idx}>
                            <td className="px-4 py-2 text-sm">{point.period}</td>
                            <td className="px-4 py-2 text-sm text-right">
                              {point.start_count.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right">
                              {point.goal_count.toLocaleString()}
                            </td>
                            <td className="px-4 py-2 text-sm text-right font-semibold text-blue-600">
                              {point.conversion_rate}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ゴール作成/編集モーダル */}
      {showGoalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {editingGoal ? 'コンバージョンゴール編集' : 'コンバージョンゴール作成'}
              </h2>
            </div>
            <div className="p-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ゴール名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={goalName}
                  onChange={(e) => setGoalName(e.target.value)}
                  placeholder="例: 成約率"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">説明</label>
                <textarea
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={goalDescription}
                  onChange={(e) => setGoalDescription(e.target.value)}
                  rows={2}
                  placeholder="ゴールの説明（任意）"
                />
              </div>

              {/* 起点パターン */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    起点パターン（分母） <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    onClick={() => addPattern('start')}
                  >
                    + 追加
                  </button>
                </div>
                <div className="space-y-2">
                  {startPatterns.map((pattern, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                        value={pattern}
                        onChange={(e) => updatePattern('start', idx, e.target.value)}
                        placeholder="例: 送信済"
                      />
                      {startPatterns.length > 1 && (
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 px-2"
                          onClick={() => removePattern('start', idx)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ラベル名の部分一致で検索されます
                </p>
              </div>

              {/* ゴールパターン */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    ゴールパターン（分子） <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    className="text-blue-600 hover:text-blue-800 text-sm"
                    onClick={() => addPattern('goal')}
                  >
                    + 追加
                  </button>
                </div>
                <div className="space-y-2">
                  {goalPatterns.map((pattern, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                        value={pattern}
                        onChange={(e) => updatePattern('goal', idx, e.target.value)}
                        placeholder="例: 成約"
                      />
                      {goalPatterns.length > 1 && (
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 px-2"
                          onClick={() => removePattern('goal', idx)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ラベル名の部分一致で検索されます（例: 「成約」は「LINE1_成約」にマッチ）
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setShowGoalModal(false)}
              >
                キャンセル
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={saveGoal}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
